import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentAPI } from '../api/api';
import { formatDateForDisplay, toISODateOrNull } from '../utils/dateUtils';
import { downloadBlob } from '../utils/fileDownload';
import { summarizeMakbuzBelgeleri } from '../utils/makbuzHesap';
import { makbuzOku } from '../utils/makbuzOku';
import './Makbuzlar.css';
import { onayla } from '../utils/onayla';

const Makbuzlar = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [makbuzTarihi, setMakbuzTarihi] = useState('');
  const [tutar, setTutar] = useState('');
  // Makbuzdan okunan / girilen ödenecek KDV ve brüt ücret (2026-10-07)
  const [kdv, setKdv] = useState('');
  const [brut, setBrut] = useState(null);
  const [okuma, setOkuma] = useState(null); // { durum, metin }
  const [dragActive, setDragActive] = useState(false);

  /* Seçilen PDF makbuz okunur: tarih, net tutar ve ödenecek KDV boşsa dolar.
     Kullanıcının yazdığının üzerine yazılmaz. */
  const dosyaSecildi = async (file) => {
    setSelectedFile(file);
    setBrut(null);
    if (!file) { setOkuma(null); return; }
    setOkuma({ durum: 'okunuyor', metin: 'Makbuz okunuyor…' });
    const s = await makbuzOku(file);
    if (s.tarih) setMakbuzTarihi((o) => o || s.tarih);
    if (s.netTutar != null) setTutar((o) => o || String(s.netTutar));
    if (s.odenecekKdv != null) setKdv((o) => o || String(s.odenecekKdv));
    setBrut(s.brutUcret ?? null);
    const okunan = [s.tarih && 'tarih', s.netTutar != null && 'net tutar', s.odenecekKdv != null && 'KDV'].filter(Boolean);
    setOkuma(okunan.length
      ? { durum: 'tamam', metin: `Makbuzdan okundu: ${okunan.join(', ')}. Kontrol edin.` }
      : { durum: 'uyari', metin: 'Makbuzdan bilgi okunamadı; elle girin.' });
  };

  useEffect(() => {
    loadDocuments();
    // Varsayılan olarak bu ayı seç
    const now = new Date();
    setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(now.getFullYear()));
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Sadece makbuzları getir
      const allDocuments = await documentAPI.getInvoices();
      
      // Makbuz tarihine göre sırala (en yeni önce)
      if (allDocuments && Array.isArray(allDocuments)) {
        allDocuments.sort((a, b) => {
          const dateA = new Date(a.FaturaTarihi ?? a.faturaTarihi ?? a.CreatedDate ?? a.createdDate ?? 0);
          const dateB = new Date(b.FaturaTarihi ?? b.faturaTarihi ?? b.CreatedDate ?? b.createdDate ?? 0);
          return dateB - dateA;
        });
      }
      
      setDocuments(allDocuments || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      alert('Belgeler yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      dosyaSecildi(file);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      dosyaSecildi(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Lütfen bir dosya seçin!');
      return;
    }

    if (!makbuzTarihi) {
      alert('Lütfen makbuz tarihi girin!');
      return;
    }

    if (!tutar || parseFloat(tutar) <= 0) {
      alert('Lütfen geçerli bir tutar girin!');
      return;
    }

    setUploading(true);
    try {
      // Makbuz tarihini UTC+3 olarak yorumlayıp UTC ISO string'e çevir
      const makbuzTarihiISO = toISODateOrNull(makbuzTarihi);
      if (!makbuzTarihiISO) {
        alert('Geçerli bir makbuz tarihi girin!');
        setUploading(false);
        return;
      }

      const kdvSayi = parseFloat(kdv);
      await documentAPI.uploadInvoice(
        selectedFile,
        description || null,
        makbuzTarihiISO,
        parseFloat(tutar),
        {
          odenecekKdv: kdv !== '' && Number.isFinite(kdvSayi) && kdvSayi >= 0 ? Math.round(kdvSayi * 100) / 100 : null,
          brutUcret: Number(brut) > 0 ? brut : null,
        }
      );

      alert('Makbuz başarıyla yüklendi!');
      setShowUploadForm(false);
      setSelectedFile(null);
      setDescription('');
      setMakbuzTarihi('');
      setTutar('');
      setKdv('');
      setBrut(null);
      setOkuma(null);
      await loadDocuments();
    } catch (error) {
      console.error('Error uploading invoice:', error);
      alert('Makbuz yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!await onayla('Bu belgeyi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await documentAPI.delete(documentId);
      alert('Belge başarıyla silindi!');
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Belge silinirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const result = await documentAPI.download(documentId);
      const blob = result?.blob ?? result;
      await downloadBlob(blob, result?.fileName || fileName || 'belge', result?.contentType || null);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Belge indirilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePreview = async (documentId) => {
    try {
      const result = await documentAPI.download(documentId);
      const blob = result?.blob ?? result;
      const mime = result?.contentType || blob?.type;
      const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;
      const url = window.URL.createObjectURL(typed);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Blob URL'i bir süre sonra serbest bırak (sekme açıldıktan sonra)
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error previewing document:', error);
      alert('Belge önizlenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Makbuz tarihine göre filtreleme
  const filteredDocuments = documents.filter(doc => {
    const makbuzTarihi = doc.FaturaTarihi ?? doc.faturaTarihi;
    if (!makbuzTarihi) return false;

    const docDate = new Date(makbuzTarihi);
    const docMonth = String(docDate.getMonth() + 1).padStart(2, '0');
    const docYear = String(docDate.getFullYear());

    if (selectedMonth && docMonth !== selectedMonth) return false;
    if (selectedYear && docYear !== selectedYear) return false;

    return true;
  });

  // Ay ve yıl seçenekleri
  const months = [
    { value: '01', label: 'Ocak' },
    { value: '02', label: 'Şubat' },
    { value: '03', label: 'Mart' },
    { value: '04', label: 'Nisan' },
    { value: '05', label: 'Mayıs' },
    { value: '06', label: 'Haziran' },
    { value: '07', label: 'Temmuz' },
    { value: '08', label: 'Ağustos' },
    { value: '09', label: 'Eylül' },
    { value: '10', label: 'Ekim' },
    { value: '11', label: 'Kasım' },
    { value: '12', label: 'Aralık' }
  ];

  const monthSummary = summarizeMakbuzBelgeleri(
    filteredDocuments
  );

  const formatMoney = (amount) =>
    `₺${(Number(amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const selectedMonthLabel =
    months.find((m) => m.value === selectedMonth)?.label || 'Tüm aylar';
  const periodLabel = `${selectedMonthLabel} ${selectedYear || ''}`.trim();

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 10; i--) {
    years.push(i);
  }

  return (
    <div className="makbuzlar-page">
      <div className="page-header">
        <h1>Makbuzlar</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowUploadForm(!showUploadForm)}
        >
          {showUploadForm ? 'Formu Kapat' : '+ Yeni Makbuz Ekle'}
        </button>
      </div>

      {showUploadForm && (
        <div className="upload-form-container">
          <h3>Yeni Makbuz Ekle</h3>
          <div className="form-group">
            <label>Makbuz Dosyası *</label>
            <div
              className={`drag-drop-area ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx';
                input.onchange = handleFileSelect;
                input.click();
              }}
            >
              {selectedFile ? (
                <div>
                  <p style={{ margin: 0, color: '#28a745', fontWeight: '600' }}>
                    ✓ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    style={{
                      marginTop: '10px',
                      padding: '5px 10px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Dosyayı Kaldır
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ margin: 0, color: '#666' }}>
                    📁 Dosyayı buraya sürükleyip bırakın veya tıklayarak seçin
                  </p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#999' }}>
                    PDF, JPG, PNG, DOC, DOCX, XLS, XLSX formatları desteklenir
                  </p>
                </div>
              )}
            </div>
          </div>
          {okuma && (
            <p className={`makbuz-okuma makbuz-okuma-${okuma.durum}`}>{okuma.metin}</p>
          )}
          <div className="form-group">
            <label>Makbuz Tarihi *</label>
            <input
              type="date"
              value={makbuzTarihi}
              onChange={(e) => setMakbuzTarihi(e.target.value)}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label>Net Tutar (makbuzdaki Net Tutar) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={tutar}
              onChange={(e) => setTutar(e.target.value)}
              className="form-control"
              placeholder="Örn: 13674.04"
              required
            />
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
              Makbuzun en altındaki Net Tutar alanını girin.
            </small>
          </div>
          <div className="form-group">
            <label>Ödenecek KDV (makbuzdaki Tahsil Edilen KDV)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={kdv}
              onChange={(e) => setKdv(e.target.value)}
              className="form-control"
              placeholder="Örn: 1519.34"
            />
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
              PDF makbuzda kendiliğinden okunur. Boş bırakılırsa net tutardan tahmin edilir
              (stopaj %20, KDV tevkifatı %50 varsayılarak); şahsa kesilen makbuzda doğru çıkmaz.
            </small>
          </div>
          <div className="form-group">
            <label>Açıklama (Opsiyonel)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control"
              rows="3"
              placeholder="Makbuz hakkında açıklama..."
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowUploadForm(false);
                setSelectedFile(null);
                setDescription('');
                setMakbuzTarihi('');
                setTutar('');
                setKdv('');
                setBrut(null);
                setOkuma(null);
              }}
            >
              İptal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !makbuzTarihi || !tutar || parseFloat(tutar) <= 0}
            >
              {uploading ? 'Yükleniyor...' : 'Yükle'}
            </button>
          </div>
        </div>
      )}

      <div className="filters-container">
        <h3>Filtreleme</h3>
        <div className="filter-row">
          <div className="form-group">
            <label>Ay</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="form-control"
            >
              <option value="">Tüm Aylar</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Yıl</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="form-control"
            >
              <option value="">Tüm Yıllar</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSelectedMonth('');
                setSelectedYear('');
                // Varsayılan olarak bu ayı seç
                const now = new Date();
                setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
                setSelectedYear(String(now.getFullYear()));
              }}
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <div className="documents-table-container">
          <div className="table-info">
            <span>Toplam: {filteredDocuments.length} belge</span>
          </div>
          <table className="documents-table">
            <thead>
              <tr>
                <th>Föy No</th>
                <th>Müvekkil</th>
                <th>Belge Adı</th>
                <th>Boyut</th>
                <th>Makbuz Tarihi</th>
                <th>Net Tutar</th>
                <th>Yükleme Tarihi</th>
                <th>Yükleyen</th>
                <th>Açıklama</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    {documents.length === 0 
                      ? 'Henüz makbuz bulunmamaktadır.' 
                      : 'Seçilen ay/yıl için makbuz bulunmamaktadır.'}
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc) => {
                  const docId = doc.Id ?? doc.id;
                  const fileName = doc.FileName ?? doc.fileName ?? 'Bilinmiyor';
                  const fileSize = doc.FileSize ?? doc.fileSize ?? 0;
                  const makbuzTarihi = doc.FaturaTarihi ?? doc.faturaTarihi;
                  const tutar = doc.Tutar ?? doc.tutar;
                  const createdDate = doc.CreatedDate ?? doc.createdDate;
                  const uploadedBy = doc.UploadedByUserName ?? doc.uploadedByUserName ?? 'Bilinmiyor';
                  const docDescription = doc.Description ?? doc.description ?? '';
                  const caseFileFoyNo = (doc.CaseFileFoyNo ?? doc.caseFileFoyNo) ?? '-';
                  const caseFileMuvekkil = (doc.CaseFileMuvekkil ?? doc.caseFileMuvekkil) ?? '-';

                  return (
                    <tr key={docId}>
                      <td>{caseFileFoyNo}</td>
                      <td>{caseFileMuvekkil}</td>
                      <td>{fileName}</td>
                      <td>{formatFileSize(fileSize)}</td>
                      <td>{makbuzTarihi ? formatDateForDisplay(makbuzTarihi) : '-'}</td>
                      <td>{tutar ? `₺${parseFloat(tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</td>
                      <td>{createdDate ? formatDateForDisplay(createdDate) : '-'}</td>
                      <td>{uploadedBy}</td>
                      <td>{docDescription || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handlePreview(docId)}
                            title="Önizleme"
                          >
                            👁️
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDownload(docId, fileName)}
                            title="İndir"
                          >
                            ⬇️
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(docId)}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className="makbuz-summary">
          <div className="makbuz-summary-header">
            <div>
              <p className="makbuz-summary-eyebrow">Aylık özet</p>
              <h2>{periodLabel || 'Seçili dönem'}</h2>
              <p className="makbuz-summary-note">
                KDV her makbuzun kendi KDV'sinden{monthSummary.tahmini > 0 ? ` · ${monthSummary.tahmini} makbuzda nete göre tahmin (stopaj %20, tevkifat %50)` : ''}
              </p>
            </div>
            <span className="makbuz-summary-count">{monthSummary.count} makbuz</span>
          </div>
          <div className="makbuz-summary-grid">
            <div className="makbuz-summary-card makbuz-summary-total">
              <span className="makbuz-summary-label">Net tutar toplamı</span>
              <strong className="makbuz-summary-value">{formatMoney(monthSummary.netTutar)}</strong>
              <span className="makbuz-summary-hint">Sisteme girdiğiniz net tutarların toplamı</span>
            </div>
            <div className="makbuz-summary-card makbuz-summary-grand">
              <span className="makbuz-summary-label">Brüt ücret toplamı</span>
              <strong className="makbuz-summary-value">{formatMoney(monthSummary.brutUcret)}</strong>
              <span className="makbuz-summary-hint">Makbuzdaki brüt; okunmayanlarda nete göre hesaplanan</span>
            </div>
            <div className="makbuz-summary-card makbuz-summary-tax">
              <span className="makbuz-summary-label">Ödenecek KDV</span>
              <strong className="makbuz-summary-value">{formatMoney(monthSummary.odenecekKdv)}</strong>
              <span className="makbuz-summary-hint">Tahsil edilen KDV (tevkifat sonrası)</span>
            </div>
          </div>
          <div className="makbuz-summary-details">
            <div><span>Toplam KDV</span><strong>{formatMoney(monthSummary.kdv)}</strong></div>
            <div><span>KDV tevkifatı</span><strong>{formatMoney(monthSummary.kdvTevkifat)}</strong></div>
            <div><span>G.V. stopaj</span><strong>{formatMoney(monthSummary.gvStopaj)}</strong></div>
            <div><span>Net ücret</span><strong>{formatMoney(monthSummary.netUcret)}</strong></div>
          </div>
          <div className="makbuz-summary-footer">
            <div>
              <span>Bu ay kestiğiniz makbuzların toplamı (brüt)</span>
              <strong>{formatMoney(monthSummary.brutUcret)}</strong>
            </div>
            <div>
              <span>Ay sonunda ödemeniz gereken vergi (KDV)</span>
              <strong>{formatMoney(monthSummary.odenecekKdv)}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Makbuzlar;
